import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $isTableCellNode } from '@lexical/table';
import {
  $getSelection,
  $isParagraphNode,
  COMMAND_PRIORITY_NORMAL,
  LexicalEditor,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';
import { useEffect } from 'react';
import {
  $containingSuggestionNode,
  $focusSuggestion,
  $getSuggestionState,
  $setSuggestionAbsolute,
  SuggestionNode,
} from '../nodes/SuggestionNode';
import $flushEditorUpdates from '../utils/flushUpdates';

export function SuggestionPlugin() {
  const [editor] = useLexicalComposerContext();
  useSuggestionCellStyle(editor);
  useSuggestionFocusStyle(editor);
  return null;
}

function useSuggestionCellStyle(editor: LexicalEditor) {
  useEffect(() => {
    return editor.registerNodeTransform(SuggestionNode, node => {
      const parent = node.getParent();
      const grandParent = parent?.getParent();

      const parentIsParagraph = $isParagraphNode(parent);
      const grandParentIsTableCell = $isTableCellNode(grandParent);
      const isInTableCell = parentIsParagraph && grandParentIsTableCell;

      const paragraphChildren = parent?.getChildren() || [];
      const isTableCellsOnlyContent = paragraphChildren.every(
        child => child === node || child.getTextContent().trim().length === 0,
      );

      const suggestionIsOnlyCellContent = isInTableCell && isTableCellsOnlyContent;

      const currentState = $getSuggestionState(node);

      if (currentState.isAbsolute !== suggestionIsOnlyCellContent) {
        $setSuggestionAbsolute(node, suggestionIsOnlyCellContent);
        $flushEditorUpdates();
      }
    });
  }, [editor]);
}

function useSuggestionFocusStyle(editor: LexicalEditor) {
  useEffect(() => {
    return editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        const selection = $getSelection();
        selection?.getNodes().forEach(node => {
          const suggestionNode = $containingSuggestionNode(node);
          if (suggestionNode) {
            $focusSuggestion(suggestionNode);
          }
        });
        return false;
      },
      COMMAND_PRIORITY_NORMAL,
    );
  });
}
